import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import _isEmpty from 'lodash/isEmpty';
import React, { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { LoadingMask, Table } from '@zillow/constellation';
import MortgageRatesTableHeader from './MortgageRatesTableHeader.js';
import MortgageRatesTableContainer from './MortgageRatesTableContainer.js';
import MortgageRateItem from './MortgageRateItem.js';

var ENDPOINT = 'https://mortgageapi.zillow.com/getRateTables';
var PROGRAMS = {
  Fixed30Year: '30-Year Fixed Rate',
  Fixed15Year: '15-Year Fixed Rate',
  Fixed20Year: '20-Year Fixed Rate',
  Fixed10Year: '10-Year Fixed Rate',
  ARM7: '7-year ARM',
  ARM5: '5-year ARM',
  ARM3: '3-year ARM'
};
var LOAN_TYPES = {
  Conventional: 'Conventional',
  VA: 'VA',
  FHA: 'FHA',
  USDA: 'USDA',
  Other: 'Other'
};
var RATE_COMPARES = {
  yesterday: '1D',
  lastWeek: '1W',
  threeMonthsAgo: '30D'
};
var LOAN_SIZES = {
  Micro: 'Micro',
  SmallConforming: 'Small Conforming',
  Conforming: 'Conforming',
  SuperConforming: 'Super Conforming',
  Jumbo: 'Jumbo'
};
function buildRequestUrl(_ref) {
  var partnerId = _ref.partnerId,
    queries = _ref.queries,
    loanToValueBucket = _ref.loanToValueBucket,
    creditScoreBucket = _ref.creditScoreBucket,
    rateLocation = _ref.rateLocation,
    refinance = _ref.refinance;
  var params = {
    partnerId: partnerId
  };
  var urlSearchParams = new URLSearchParams(params);
  queries.forEach(function (query, index) {
    var key = "q" + index;
    var queryKey = "queries." + key;
    if (query.program) {
      urlSearchParams.append(queryKey + ".program", query.program);
    }
    if (query.loanType) {
      urlSearchParams.append(queryKey + ".loanType", query.loanType);
    }
    if (query.loanSize) {
      urlSearchParams.append(queryKey + ".loanAmountBucket", query.loanSize);
    }
    if (loanToValueBucket) {
      urlSearchParams.append(queryKey + ".loanToValueBucket", loanToValueBucket);
    }
    if (creditScoreBucket) {
      urlSearchParams.append(queryKey + ".creditScoreBucket", creditScoreBucket);
    }
    if (rateLocation) {
      urlSearchParams.append(queryKey + ".stateAbbreviation", rateLocation);
    }
    urlSearchParams.append(queryKey + ".refinance", refinance);
  });
  return ENDPOINT + '?' + urlSearchParams.toString();
}
function getMortgageProgramLabel(_ref2) {
  var program = _ref2.program,
    loanType = _ref2.loanType,
    loanSize = _ref2.loanSize;
  var programLabel = PROGRAMS[program];
  if (loanType && loanType !== 'Conventional') {
    programLabel += " " + LOAN_TYPES[loanType];
  }
  if (loanSize && loanSize !== 'Conforming') {
    programLabel += " " + LOAN_SIZES[loanSize];
  }
  return programLabel;
}
function formatRates(_ref3) {
  var responseRates = _ref3.responseRates,
    queries = _ref3.queries,
    rateCompare = _ref3.rateCompare;
  return Object.keys(responseRates).length > 0 ? queries.map(function (_ref4, index) {
    var _responseRates$key, _responseRates$key2, _responseRates$key3, _responseRates$key4;
    var program = _ref4.program,
      loanType = _ref4.loanType,
      loanSize = _ref4.loanSize,
      link = _ref4.link;
    var key = "q" + index;
    var programLabel = getMortgageProgramLabel({
      program: program,
      loanType: loanType,
      loanSize: loanSize
    });
    return {
      program: programLabel,
      link: link || null,
      rate: !!responseRates[key].today.rate ? responseRates[key].today.rate.toFixed(2) : responseRates[key].today.rate,
      lastUpdated: (_responseRates$key = responseRates[key]) === null || _responseRates$key === void 0 || (_responseRates$key = _responseRates$key.today) === null || _responseRates$key === void 0 ? void 0 : _responseRates$key.time,
      loanAmount: ((_responseRates$key2 = responseRates[key]) === null || _responseRates$key2 === void 0 || (_responseRates$key2 = _responseRates$key2.todayPayment) === null || _responseRates$key2 === void 0 ? void 0 : _responseRates$key2.loanAmount) || 0,
      downPayment: ((_responseRates$key3 = responseRates[key]) === null || _responseRates$key3 === void 0 || (_responseRates$key3 = _responseRates$key3.todayPayment) === null || _responseRates$key3 === void 0 ? void 0 : _responseRates$key3.downPayment) || 0,
      monthlyPayment: ((_responseRates$key4 = responseRates[key]) === null || _responseRates$key4 === void 0 || (_responseRates$key4 = _responseRates$key4.todayPayment) === null || _responseRates$key4 === void 0 ? void 0 : _responseRates$key4.monthlyPayment) || 0,
      isRateChangeUp: responseRates[key].today.rate > responseRates[key][rateCompare].rate,
      rateChange: Math.abs(responseRates[key][rateCompare].rate - responseRates[key].today.rate).toFixed(2),
      apr: responseRates[key].today.apr.toFixed(2),
      isAprChangeUp: responseRates[key].today.apr > responseRates[key][rateCompare].apr,
      aprChange: Math.abs(responseRates[key].today.apr - responseRates[key][rateCompare].apr).toFixed(2)
    };
  }) : [];
}
function useMortgageRatesTable(_ref5) {
  var partnerId = _ref5.partnerId,
    queries = _ref5.queries,
    loanToValueBucket = _ref5.loanToValueBucket,
    creditScoreBucket = _ref5.creditScoreBucket,
    refinance = _ref5.refinance,
    rateLocation = _ref5.rateLocation,
    rateCompare = _ref5.rateCompare,
    nextData = _ref5.nextData;
  var _useState = useState((nextData === null || nextData === void 0 ? void 0 : nextData.rates) || []),
    _useState2 = _slicedToArray(_useState, 2),
    rates = _useState2[0],
    setRates = _useState2[1];
  var _useState3 = useState(true),
    _useState4 = _slicedToArray(_useState3, 2),
    isLoading = _useState4[0],
    setIsLoading = _useState4[1];
  useEffect(function () {
    setIsLoading(true);
    // Skip fetching if we already have data.
    if (!_isEmpty(nextData === null || nextData === void 0 ? void 0 : nextData.rates)) {
      setIsLoading(false);
      return;
    }
    var requestUrl = buildRequestUrl({
      partnerId: partnerId,
      queries: queries,
      loanToValueBucket: loanToValueBucket,
      creditScoreBucket: creditScoreBucket,
      refinance: refinance,
      rateLocation: rateLocation
    });
    fetch(requestUrl).then(function (response) {
      return response.json();
    }).then(function (data) {
      var responseRates = data === null || data === void 0 ? void 0 : data.rates;
      // Format the rates.
      var formattedRates = formatRates({
        responseRates: responseRates,
        queries: queries,
        rateCompare: rateCompare
      });
      setRates(formattedRates);
      setIsLoading(false);
    });
  }, [JSON.stringify(queries), refinance, loanToValueBucket, creditScoreBucket, rateLocation]);
  return {
    isLoading: isLoading,
    rates: rates
  };
}
var MortgageRatesTable = function MortgageRatesTable(_ref6) {
  var _Date, _rates$;
  var columns = _ref6.columns,
    refinance = _ref6.refinance,
    partnerId = _ref6.partnerId,
    queries = _ref6.queries,
    loanToValueBucket = _ref6.loanToValueBucket,
    creditScoreBucket = _ref6.creditScoreBucket,
    rateLocation = _ref6.rateLocation,
    rateCompare = _ref6.rateCompare,
    nextData = _ref6.nextData;
  var _useMortgageRatesTabl = useMortgageRatesTable({
      refinance: refinance,
      partnerId: partnerId,
      queries: queries,
      loanToValueBucket: loanToValueBucket,
      creditScoreBucket: creditScoreBucket,
      rateLocation: rateLocation,
      rateCompare: rateCompare,
      nextData: nextData
    }),
    isLoading = _useMortgageRatesTabl.isLoading,
    rates = _useMortgageRatesTabl.rates;
  return /*#__PURE__*/React.createElement(LoadingMask, {
    loading: isLoading,
    display: 'block',
    "aria-busy": isLoading
  }, /*#__PURE__*/React.createElement(MortgageRatesTableContainer, {
    updatedAt: ((_Date = new Date((_rates$ = rates[0]) === null || _rates$ === void 0 ? void 0 : _rates$.lastUpdated)) === null || _Date === void 0 ? void 0 : _Date.toLocaleDateString()) || 'N/A',
    loanToValueBucket: loanToValueBucket,
    creditScoreBucket: creditScoreBucket,
    rates: rates,
    refinance: refinance
  }, /*#__PURE__*/React.createElement(MortgageRatesTableHeader, {
    rateCompare: rateCompare,
    columns: columns
  }), /*#__PURE__*/React.createElement(Table.Body, null, isLoading ? /*#__PURE__*/React.createElement(Table.Row, null, /*#__PURE__*/React.createElement(Table.Cell, {
    colSpan: columns.length + 1
  }, "Loading...")) : rates.map(function (formattedRate, index) {
    return /*#__PURE__*/React.createElement(MortgageRateItem, {
      rate: formattedRate,
      columns: columns,
      key: index
    });
  }))));
};

// For the editor.
MortgageRatesTable.Container = MortgageRatesTableContainer;
MortgageRatesTable.Header = MortgageRatesTableHeader;
MortgageRatesTable.Item = MortgageRateItem;

// For Next.js we can use the following function to fetch data at build time.
MortgageRatesTable.fetchMortgageRates = function (_ref7) {
  return new Promise(function ($return, $error) {
    var partnerId, queries, loanToValueBucket, creditScoreBucket, refinance, rateCompare, rateLocation, requestUrl, response, data;
    partnerId = _ref7.partnerId, queries = _ref7.queries, loanToValueBucket = _ref7.loanToValueBucket, creditScoreBucket = _ref7.creditScoreBucket, refinance = _ref7.refinance, rateCompare = _ref7.rateCompare, rateLocation = _ref7.rateLocation;
    requestUrl = buildRequestUrl({
      partnerId: partnerId,
      queries: queries,
      loanToValueBucket: loanToValueBucket,
      creditScoreBucket: creditScoreBucket,
      refinance: refinance,
      rateLocation: rateLocation
    });
    return Promise.resolve(fetch(requestUrl)).then(function ($await_1) {
      try {
        response = $await_1;
        return Promise.resolve(response.json()).then(function ($await_2) {
          try {
            data = $await_2;
            return $return(formatRates({
              responseRates: data === null || data === void 0 ? void 0 : data.rates,
              queries: queries,
              rateCompare: rateCompare
            }));
          } catch ($boundEx) {
            return $error($boundEx);
          }
        }, $error);
      } catch ($boundEx) {
        return $error($boundEx);
      }
    }, $error);
  });
};
MortgageRatesTable.propTypes = process.env.NODE_ENV !== "production" ? {
  columns: PropTypes.arrayOf(PropTypes.string),
  refinance: PropTypes.bool,
  partnerId: PropTypes.string.isRequired,
  queries: PropTypes.arrayOf(PropTypes.shape({
    program: PropTypes.string,
    loanType: PropTypes.string,
    loanSize: PropTypes.string,
    link: PropTypes.string
  })),
  loanToValueBucket: PropTypes.string,
  creditScoreBucket: PropTypes.string,
  rateLocation: PropTypes.string
} : {};
MortgageRatesTable.defaultProps = {
  columns: ['rate', 'rateChange', 'apr', 'aprChange'],
  refinance: false,
  queries: [{
    program: 'Fixed30Year'
  }],
  rateCompare: 'lastWeek',
  loanToValueBucket: 'Normal',
  creditScoreBucket: 'VeryHigh',
  rateLocation: 'US'
};
var MortgageRatesTable$1 = MortgageRatesTable;

export { LOAN_SIZES, LOAN_TYPES, PROGRAMS, RATE_COMPARES, MortgageRatesTable$1 as default, formatRates, getMortgageProgramLabel, useMortgageRatesTable };
